import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DomainEventDetailDto,
  EventDeliveryStatus,
  EventStreamDto,
} from '@cpm/shared';
import { Activity, Play, RotateCcw } from 'lucide-react';
import { Fragment, useState } from 'react';
import { api } from '../api/client.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  PageHeader,
  Spinner,
  cx,
  type BadgeTone,
} from '../components/ui.js';

const STATUS_TONE: Record<EventDeliveryStatus, BadgeTone> = {
  succeeded: 'green',
  pending: 'gray',
  failed: 'yellow',
  dead: 'red',
};

function Tile({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <Card className="px-5 py-4">
      <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
      <p className={cx('mt-1 text-2xl font-semibold', tone)}>{value}</p>
    </Card>
  );
}

function DeliveryList({ eventId }: { eventId: string }) {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['events', eventId],
    queryFn: () => api.get<DomainEventDetailDto>(`/api/events/${eventId}`),
  });
  const redeliver = useMutation({
    mutationFn: (deliveryId: string) =>
      api.post(`/api/events/deliveries/${deliveryId}/redeliver`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });

  if (detail.isPending) return <Spinner label="Loading deliveries…" />;
  if (detail.error) return <ErrorNote error={detail.error} />;

  return (
    <div className="space-y-3 border-t border-slate-100 p-5 dark:border-slate-800">
      <pre className="max-h-40 overflow-auto rounded-lg bg-slate-950 p-3 text-xs text-slate-200">
        {JSON.stringify(detail.data.event.payload, null, 2)}
      </pre>
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          <tr>
            <th className="py-2 font-medium">Subscriber</th>
            <th className="py-2 font-medium">Status</th>
            <th className="py-2 font-medium">Attempts</th>
            <th className="py-2 font-medium">Last error</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {detail.data.deliveries.map((d) => (
            <tr key={d.id}>
              <td className="py-2 font-mono text-xs">{d.subscriber}</td>
              <td className="py-2">
                <Badge tone={STATUS_TONE[d.status]}>{d.status}</Badge>
              </td>
              <td className="py-2">
                {d.attempts}/{d.maxAttempts}
              </td>
              <td className="max-w-xs truncate py-2 text-xs text-slate-500 dark:text-slate-400">
                {d.lastError ?? '—'}
              </td>
              <td className="py-2 text-right">
                {(d.status === 'failed' || d.status === 'dead') && (
                  <Button
                    variant="secondary"
                    className="px-2 py-1 text-xs"
                    loading={redeliver.isPending}
                    onClick={() => redeliver.mutate(d.id)}
                  >
                    <RotateCcw className="size-3.5" /> Redeliver
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {redeliver.error != null && <ErrorNote error={redeliver.error} />}
    </div>
  );
}

export function EventsPage() {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const stream = useQuery({
    queryKey: ['events', 'stream'],
    queryFn: () => api.get<EventStreamDto>('/api/events'),
    refetchInterval: (query) => (query.state.data?.draining ? 1_500 : 15_000),
  });
  const drain = useMutation({
    mutationFn: () => api.post('/api/events/drain'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['events'] }),
  });

  return (
    <div>
      <PageHeader
        title="Event bus"
        description="Durable domain-event delivery. Automation consumers react here; failed deliveries retry with backoff, then dead-letter for an operator."
        actions={
          <Button loading={drain.isPending} onClick={() => drain.mutate()}>
            <Play className="size-4" /> Drain now
          </Button>
        }
      />
      {drain.error != null && (
        <div className="mb-4">
          <ErrorNote error={drain.error} />
        </div>
      )}

      {stream.isPending ? (
        <Spinner />
      ) : stream.error ? (
        <ErrorNote error={stream.error} />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Tile label="Pending" value={stream.data.pending} tone="text-slate-700 dark:text-slate-200" />
            <Tile label="Failed (retrying)" value={stream.data.failed} tone="text-amber-600" />
            <Tile label="Dead-lettered" value={stream.data.dead} tone="text-red-600" />
          </div>

          <Card>
            <CardHeader
              title="Recent events"
              description="Newest first — expand a row to inspect its payload and per-subscriber deliveries."
            />
            {stream.data.events.length === 0 ? (
              <EmptyState
                icon={<Activity className="size-6" />}
                title="No events yet"
                description="Domain events appear as mutations publish them."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                    <tr>
                      <th className="px-5 py-3 font-medium">Type</th>
                      <th className="px-5 py-3 font-medium">Deliveries</th>
                      <th className="px-5 py-3 font-medium">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {stream.data.events.map((e) => (
                      <Fragment key={e.id}>
                        <tr
                          className="cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                          onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                        >
                          <td className="px-5 py-3 font-mono text-xs">{e.type}</td>
                          <td className="px-5 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(Object.entries(e.deliveryCounts) as Array<[EventDeliveryStatus, number]>)
                                .filter(([, n]) => n > 0)
                                .map(([status, n]) => (
                                  <Badge key={status} tone={STATUS_TONE[status]}>
                                    {n} {status}
                                  </Badge>
                                ))}
                              {Object.values(e.deliveryCounts).every((n) => n === 0) && (
                                <span className="text-xs text-slate-400">no consumers</span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                            {new Date(e.createdAt).toLocaleString()}
                          </td>
                        </tr>
                        {expanded === e.id && (
                          <tr>
                            <td colSpan={3} className="p-0">
                              <DeliveryList eventId={e.id} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
